// Copyright 2026, University of Maryland, All Rights Reserved
/**
 * DocumentAnalysisPanel — dispatcher that:
 *  1. Looks up the renderer's component in the registry.
 *  2. For `versionMode: 'single'`, fetches dataset-slot payloads at the chosen
 *     version and surfaces "missing data" UI consistently.
 *  3. Renders the registered component with the resolved props.
 *
 * Components are pure: they receive renderer config + slot data + version
 * context and render. They do not detect their own type.
 */
import { type FC } from 'react'
import { AlertCircle, Loader2 } from 'lucide-react'
import { Alert, AlertDescription, Card, CardContent } from '@/components/ui'

import { useServices } from '@/app-shell/services'
import { getRendererComponent, useRendererSlotData } from '@/app-shell/renderer-components'
import type { WorkspaceRenderer } from '@/subsystems/agents'

interface Props {
  renderer: WorkspaceRenderer
  documentId: string
  documentName?: string
  orgId: string
  workspaceId: string
  /**
   * Pipeline id of the agent that mounts this renderer. Caller is responsible
   * for sourcing it from the agent record; this dispatcher only plumbs it
   * through to renderer components that need it.
   */
  pipelineId?: string
  /** Selected version for `versionMode: 'single'` components. */
  version?: number
  /** Selected old version for `versionMode: 'compare'` components. */
  oldVersion?: number
  /** Selected new version for `versionMode: 'compare'` components. */
  newVersion?: number
}

export const DocumentAnalysisPanel: FC<Props> = ({
  renderer,
  documentId,
  documentName,
  orgId,
  workspaceId,
  pipelineId,
  version,
  oldVersion,
  newVersion,
}) => {
  const services = useServices()
  const apiClient = services.getClient()

  const componentDef = getRendererComponent(renderer.component)
  const isSingle = componentDef?.versionMode === 'single'

  const { slotData, loading, error, missingSlotKeys } = useRendererSlotData({
    apiClient,
    renderer,
    documentId,
    orgId,
    workspaceId,
    agentId: renderer.agentId,
    version,
    enabled: isSingle,
  })

  if (!componentDef) {
    return (
      <Card>
        <CardContent className="p-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>
              Unsupported renderer component: <code>{renderer.component ?? '(none)'}</code>
            </AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  if (isSingle && loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center p-8">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    )
  }

  if (isSingle && error) {
    return (
      <Card>
        <CardContent className="p-4">
          <Alert variant="destructive">
            <AlertCircle className="h-4 w-4" />
            <AlertDescription>{error}</AlertDescription>
          </Alert>
        </CardContent>
      </Card>
    )
  }

  return (
    <div className="flex-1 flex flex-col min-h-0">
      {isSingle && missingSlotKeys.length > 0 && version !== undefined && (
        <Alert variant="destructive" className="mb-3 shrink-0">
          <AlertDescription>
            Missing data for v{version}: {missingSlotKeys.join(', ')}.
            Re-run the agent on v{version} to populate.
          </AlertDescription>
        </Alert>
      )}
      <div className="flex-1 min-h-0 flex flex-col">
        <componentDef.Component
          renderer={renderer}
          documentId={documentId}
          documentName={documentName}
          orgId={orgId}
          workspaceId={workspaceId}
          agentId={renderer.agentId}
          apiClient={apiClient}
          pipelineId={pipelineId}
          version={version}
          oldVersion={oldVersion}
          newVersion={newVersion}
          slotData={isSingle ? slotData : undefined}
        />
      </div>
    </div>
  )
}
